import { translationToolProjects } from '@/shared';
import i18n from '../i18n';
import store from '../store';

const ignoreKeys = ['defaultIntroductionText'];

const replaceVariableFormat = (text) => text
  .replace(/{{/g, '{')
  .replace(/}}/g, '}')
  .replace(/<span translate="no">|<\/span>/g, '');

const replaceVariableTagOnly = (text) => text.replace(/<span translate="no">|<\/span>/g, '');

const updateNestedObject = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'string') {
      /* eslint-disable no-param-reassign */
      if (ignoreKeys.includes(key)) {
        obj[key] = replaceVariableTagOnly(obj[key]);
      } else {
        obj[key] = replaceVariableFormat(obj[key]);
      }
    } else if (typeof obj[key] === 'object') {
      updateNestedObject(obj[key]);
    }
  });
};

const TranslationHelper = {

  async updateTranslations(router, language = 'en', force = false) {
    this.$store = this.$store ?? router.app.$store;
    const projectsPages = {
      neoFront: ['login', 'reservations', 'mealPlans', 'booking', 'calendar', 'roomTypes', 'ratePlans',
        'masterData', 'contracts', 'contactPersons', 'nearby', 'facilities', 'policies', 'photos', 'onlinepresence',
        'channels', 'channel', 'systems', 'hotels', 'group', 'editGroup', 'description', 'adminGroups', 'adminGroup',
        'promotions', 'groups', 'channelshealth', 'invoices', 'setupWizard', 'profile', 'payments', 'settings',
        'dashboard', 'validation', 'links', 'datepicker', 'date', 'buttons', 'langs', 'legal', 'navbarTitles', 'auth',
        'restrictions', 'guestsPrices', 'commonKeys', 'productsSelector', 'addr', 'permissions', 'menu', 'properties',
        'customTable', 'feedbackForm', 'placeholder', 'emailChange', 'passwordReset', 'register', 'passwordEmail',
        'details', 'labels', 'webhooks', 'maintenance', 'loadingPerformanceReports', 'extras', 'errors', 'users',
        'marketPlace', 'partners', 'errorReports', 'activityReports', 'bookingEngine', 'messages', 'mappingMaster',
        'generalsettings', 'page', 'dynamicTranslations', 'emailTemplate', 'passwords', 'calendarComponent',
        'businessCategories', 'explanationBox', 'follower', 'consultants', 'billingAddress', 'reports', 'events',
        'billing', 'userProperties', 'mediaCategories',
      ],
      cancellationRules: [
        'cancellationRules',
      ],
    };

    let data = await this.$store.getters['translation/getLanguages'];
    if (!data || force) {
      const projects = [];
      translationToolProjects.forEach((project) => {
        projects.push({
          key: project,
          pages: projectsPages[project],
        });
      });

      data = await this.$store.dispatch(
        'translation/getTranslate', {
          projects,
          language,
          force,
        },
      );
    }

    this.$store.commit('translation/getTranslation', { ...data.data });
    if (data.status === 200 || data?.data?.translations) {
      let locales = {};
      const { translations } = data.data;

      translations.forEach((trans) => {
        translationToolProjects.forEach((project) => {
          if (project in trans) {
            Object.entries(trans[project]).forEach(([k, v]) => {
              locales[k] = (k in locales) ? { ...locales[k], ...v } : { ...v };
            });
          }
        });
      });

      locales = JSON.parse(JSON.stringify(locales));

      Object.keys(locales)
        .forEach((locale) => {
          updateNestedObject(locales[locale]);
          i18n.setLocaleMessage(locale, { ...locales[locale] });
        });
    } else {
      const locales = require.context('../i18n/locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
      const messages = {};
      locales.keys().forEach((key) => {
        const matched = key.match(/([A-Za-z0-9-_]+)\./i);
        if (matched && matched.length > 1) {
          const locale = matched[1];
          messages[locale] = locales(key);
          i18n.setLocaleMessage(locale, messages[locale]);
        }
      });
    }
  },
};

function findPageByName(pages, name) {
  let foundPage = null;

  pages.some((page) => {
    if (page.name === name) {
      foundPage = page;
      return true;
    }
    if (page.sub_pages && page.sub_pages.length > 0) {
      foundPage = findPageByName(page.sub_pages, name);
      return foundPage !== null;
    }
    return false;
  });

  return foundPage;
}

export const getLangInfoKey = (model, code, translating = false, mainLanguage = 'en') => {
  if (code === mainLanguage) return 'noTranslation';
  if (!model) return null;
  const lang = model.langs[code];
  if (!(lang && Object.prototype.hasOwnProperty.call(lang, 'protected') && lang.protected !== '')) return null;
  if (lang.protected) return 'manualTranslation';
  return translating ? 'autoTranslating' : 'autoTranslation';
};

export const setPreference = async ({ page, itemIds, languages }) => {
  const { pageTree } = await store.getters['user/property'];
  const pageData = findPageByName(pageTree, page);
  if (pageData) {
    await store.dispatch(
      'translation/setPreference', {
        pageId: pageData.id,
        itemIds: Array.isArray(itemIds) ? itemIds : [itemIds],
        languages,
      },
    );
  }
};

export const getPreference = async (page, itemId = null) => {
  const { pageTree } = await store.getters['user/property'];
  const pageData = findPageByName(pageTree, page);
  if (pageData) {
    const { data } = await store.dispatch(
      'translation/getPreference', {
        pageId: pageData.id,
        itemId,
      },
    );
    return data?.result?.languages;
  }
  return undefined;
};

export const removePreference = async (page, itemId = null) => {
  const { pageTree } = await store.getters['user/property'];
  const pageData = findPageByName(pageTree, page);
  if (pageData) {
    await store.dispatch(
      'translation/removePreference', {
        pageId: pageData.id,
        itemId,
      },
    );
  }
};

export default TranslationHelper;
